import React from 'react';
import { motion } from 'framer-motion';
import { 
  Printer, FileText, Mail, 
  Filter, CheckCircle, ArrowDown,
  DollarSign, FileInput, ScanLine
} from 'lucide-react';
import { Badge } from '../ui/badge';

const EOBStreamliningSlide = () => {
  return (
    <div className="h-full w-full bg-slate-900 flex items-center justify-center p-12 relative overflow-hidden text-white">
        {/* Abstract Background */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-emerald-600/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2" />

        {/* Floating Agent Header */}
        <div className="absolute top-8 left-8 lg:left-12 z-50 flex items-center gap-4 bg-white/10 backdrop-blur-md p-2 pr-6 rounded-full border border-white/10 shadow-lg">
            <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-slate-700 shadow-sm">
                 <img src="/assets/alisha.png" alt="Alisha" className="w-full h-full object-cover" />
            </div>
            <div className="text-left">
                <h3 className="text-lg font-bold text-white leading-none">Alisha</h3>
                <p className="text-xs text-emerald-400 font-bold uppercase tracking-wide">EOB Posting Agent</p>
            </div>
        </div>

        <div className="flex flex-row h-full w-full max-w-[1600px] gap-12 relative z-10 pt-0">
            {/* Left Side: Context */}
            <div className="w-1/3 flex flex-col justify-center space-y-6">
                <h2 className="text-4xl lg:text-5xl font-bold leading-tight">
                    We streamline <span className="text-emerald-400">all your EOBs</span> into one unified flow.
                </h2>
                
                <p className="text-lg text-slate-300 leading-relaxed">
                   Whether it's a fax, a paper check in the mail, or a digital ERA, Alisha handles it all. Plus, we manage your ERA enrollment with payers so you only have to fill out one form, once.
                </p>

                <div className="grid grid-cols-1 gap-4 mt-4">
                     <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700 flex gap-4 items-center">
                        <div className="bg-blue-500/20 p-2 rounded-lg text-blue-400">
                            <FileInput className="w-6 h-6" />
                        </div>
                        <div>
                            <h4 className="font-bold text-white">One-Click ERA Enrollment</h4>
                            <p className="text-xs text-slate-400">We handle enrollment with thousands of payers.</p>
                        </div>
                     </div>
                     <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700 flex gap-4 items-center">
                        <div className="bg-emerald-500/20 p-2 rounded-lg text-emerald-400">
                            <ScanLine className="w-6 h-6" />
                        </div>
                        <div>
                            <h4 className="font-bold text-white">Universal Ingestion</h4>
                            <p className="text-xs text-slate-400">Fax, Paper, or Digital - we read it all.</p>
                        </div>
                     </div>
                </div>
            </div>

            {/* Right Side: Funnel Visualization */}
            <div className="w-2/3 h-full flex items-center justify-center">
                <div className="w-full max-w-3xl h-[600px] relative flex flex-col items-center justify-center">
                    
                    {/* Inputs Row */}
                    <div className="flex gap-6 w-full justify-center mb-8">
                        <DataSource 
                            icon={Printer} 
                            label="Incoming Faxes" 
                            sub="AI-read instantly"
                            color="blue"
                            delay={0} 
                        />
                        <DataSource 
                            icon={FileText} 
                            label="Digital ERAs" 
                            sub="Auto-downloaded"
                            color="emerald"
                            delay={0.2} 
                        />
                        <DataSource 
                            icon={Mail} 
                            label="Paper Mail" 
                            sub="Scanned & OCR'd"
                            color="amber"
                            delay={0.4} 
                        />
                    </div>

                    {/* Funnel Graphic */}
                    <div className="relative w-full max-w-[500px] h-[200px] flex justify-center">
                        {/* Animated Streams */}
                        <StreamLine color="bg-blue-500" left="20%" delay={0.5} />
                        <StreamLine color="bg-emerald-500" left="50%" delay={0.7} />
                        <StreamLine color="bg-amber-500" left="80%" delay={0.9} />

                        {/* Funnel Shape */}
                        <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-slate-900 border-x border-b border-slate-700 rounded-b-full backdrop-blur-sm z-10 flex items-center justify-center">
                             <div className="text-center">
                                <Filter className="w-12 h-12 text-slate-600 mx-auto mb-2" />
                                <span className="text-xs uppercase tracking-widest text-slate-500 font-bold">Normalization Engine</span>
                             </div>
                        </div>
                    </div>

                    {/* Output */}
                    <motion.div 
                        initial={{ scale: 0.9, opacity: 0 }}
                        whileInView={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 1.2 }}
                        className="mt-8 w-full max-w-[400px] bg-emerald-950/30 border border-emerald-500/30 rounded-2xl p-6 relative overflow-hidden text-center"
                    >
                        <div className="absolute inset-0 bg-emerald-500/5 animate-pulse" />
                        
                        <div className="relative z-10">
                            <div className="flex justify-center mb-4">
                                <div className="bg-emerald-500 rounded-full p-3 shadow-lg shadow-emerald-500/20">
                                    <CheckCircle className="w-8 h-8 text-white" />
                                </div>
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-2">Unified Data Stream</h3>
                            <p className="text-slate-400 text-sm">All payment data standardized and ready for posting.</p>
                        </div>
                    </motion.div>

                </div>
            </div>
        </div>
    </div>
  );
};

const DataSource = ({ icon: Icon, label, sub, color, delay }) => {
    const colorClasses = {
        blue: "bg-blue-500/10 border-blue-500/20 text-blue-400",
        emerald: "bg-emerald-500/10 border-emerald-500/20 text-emerald-400",
        amber: "bg-amber-500/10 border-amber-500/20 text-amber-400"
    };

    return (
        <motion.div 
            initial={{ y: -20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ delay }}
            className={`flex-1 max-w-[200px] p-6 rounded-xl border flex flex-col items-center text-center gap-3 ${colorClasses[color]}`}
        >
            <Icon className="w-8 h-8" />
            <div>
                <h4 className="font-bold text-white text-sm">{label}</h4>
                <p className="text-xs opacity-70 mt-1">{sub}</p>
            </div>
        </motion.div>
    );
};

const StreamLine = ({ color, left, delay }) => (
    <motion.div
        className={`absolute top-0 w-1 h-full ${color} opacity-50 blur-[2px]`}
        style={{ left }}
        initial={{ height: 0 }}
        whileInView={{ height: '100%' }}
        transition={{ delay, duration: 0.5 }}
    />
);

export default EOBStreamliningSlide;
